import * as React from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from '@mui/material/styles';
import { grey, red } from '@mui/material/colors';
import { Paper, CircularProgress, Link } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

import { useAuth } from '../../../components/AuthAdminProvider';
import theme from './themeLogin';
import backgroundImg from '../../../components/imgs/background.jpg';
import { getDeviceName } from '../../../services/fingerprintService';

export default function Verify2FAPage() {
  const navigate = useNavigate();
  const auth = useAuth();

  const [code, setCode] = React.useState('');
  const [trustDevice, setTrustDevice] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(0);
  const [message, setMessage] = React.useState({ text: '', severity: 'info', show: false });

  React.useEffect(() => {
    document.title = "IPV7 Anubis - Verificacao 2FA";
  }, []);

  // Countdown timer para cooldown de reenvio
  React.useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  // Se nao ha sessao 2FA pendente, redireciona para login
  if (!auth.pending2FA) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage({ text: '', severity: 'info', show: false });

    if (code.length !== 6) {
      setMessage({ text: 'Digite o codigo de 6 digitos', severity: 'warning', show: true });
      return;
    }

    setLoading(true);

    try {
      const result = await auth.verify2FA(code, trustDevice, getDeviceName());

      if (result.success) {
        navigate('/admin', { replace: true });
      } else {
        // Tratar diferentes tipos de erro
        let errorMsg = '';
        let severity = 'error';

        switch (result.error) {
          case 'expired':
          case 'invalid_token':
            errorMsg = 'Sessao expirada. Por favor, faca login novamente.';
            // Redirecionar para login apos 2 segundos
            setTimeout(() => auth.cancel2FA(), 2000);
            break;
          case 'max_attempts':
            errorMsg = 'Numero maximo de tentativas excedido. Faca login novamente.';
            setTimeout(() => auth.cancel2FA(), 2000);
            break;
          case 'invalid_code':
            errorMsg = 'Codigo incorreto.';
            if (result.attemptsRemaining !== undefined && result.attemptsRemaining !== null) {
              if (result.attemptsRemaining === 1) {
                errorMsg += ' Ultima tentativa!';
                severity = 'warning';
              } else if (result.attemptsRemaining > 0) {
                errorMsg += ` ${result.attemptsRemaining} tentativas restantes.`;
              }
            }
            break;
          default:
            errorMsg = result.message || 'Erro ao verificar codigo. Tente novamente.';
        }

        setMessage({ text: errorMsg, severity, show: true });
        setCode('');
      }
    } catch (err) {
      setMessage({ text: 'Erro de conexao. Verifique sua internet e tente novamente.', severity: 'error', show: true });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setMessage({ text: '', severity: 'info', show: false });

    try {
      const result = await auth.resend2FACode();

      if (result.success) {
        setMessage({ text: 'Codigo reenviado com sucesso!', severity: 'success', show: true });
        setCooldown(60); // 60 segundos de cooldown
      } else {
        if (result.waitSeconds) {
          setCooldown(result.waitSeconds);
          setMessage({ text: `Aguarde ${result.waitSeconds}s para reenviar`, severity: 'warning', show: true });
        } else {
          setMessage({ text: result.message || 'Erro ao reenviar codigo', severity: 'error', show: true });
        }
      }
    } catch (err) {
      setMessage({ text: 'Erro ao reenviar codigo', severity: 'error', show: true });
    } finally {
      setResendLoading(false);
    }
  };

  const handleCancel = () => {
    auth.cancel2FA();
  };

  return (
    <ThemeProvider theme={theme}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'end',
          justifyContent: 'center',
          height: '100vh',
          backgroundImage: `url(${backgroundImg})`,
          backgroundPosition: 'left',
          backgroundSize: 'cover',
        }}
      >
        <CssBaseline />
        <Box minWidth={300} sx={{ display: 'flex' }}>
          <Box
            component="div"
            sx={{
              height: '100vh',
              m: 0,
              backgroundColor: "#C4161C",
              opacity: 0.99,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Paper
              elevation={2}
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                mt: 1,
                maxHeight: 550,
                width: 450,
                p: 4,
                m: 5,
                backgroundColor: 'white',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: grey[100],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2
                  }}
                >
                  <LockOutlinedIcon sx={{ fontSize: 30, color: grey[700] }} />
                </Box>
                <Typography component="h1" variant="h5" align="center">
                  Verificacao em Duas Etapas
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, p: 2, backgroundColor: grey[50], borderRadius: 1 }}>
                <EmailOutlinedIcon sx={{ mr: 1, color: grey[600] }} />
                <Typography variant="body2" color="text.secondary">
                  Enviamos um codigo para <strong>{auth.pending2FA?.maskedEmail}</strong>
                </Typography>
              </Box>

              <TextField
                margin="normal"
                required
                fullWidth
                id="code"
                label="Codigo de Verificacao"
                name="code"
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={handleCodeChange}
                inputProps={{
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
                }}
                placeholder="000000"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    Confiar neste dispositivo por 30 dias
                  </Typography>
                }
                sx={{ mt: 1 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || code.length !== 6}
                sx={{
                  mt: 3,
                  mb: 2,
                  backgroundColor: grey[900],
                  '&:hover': { backgroundColor: grey[800] }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verificar'}
              </Button>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Button
                  onClick={handleResend}
                  disabled={resendLoading || cooldown > 0}
                  sx={{ textTransform: 'none', color: grey[700] }}
                >
                  {resendLoading ? (
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                  ) : null}
                  {cooldown > 0 ? `Reenviar em ${cooldown}s` : 'Reenviar codigo'}
                </Button>

                <Link
                  component="button"
                  type="button"
                  onClick={handleCancel}
                  sx={{ color: red[700], textDecoration: 'none', cursor: 'pointer' }}
                >
                  Cancelar
                </Link>
              </Box>

              {message.show && (
                <Alert severity={message.severity} sx={{ mt: 2 }}>
                  {message.text}
                </Alert>
              )}

              <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 2 }}>
                O codigo expira em 5 minutos
              </Typography>
            </Paper>
          </Box>
        </Box>
      </div>
    </ThemeProvider>
  );
}
